/**
 * ProductDetailDrawer — product detail + per-location inventory snapshot,
 * polished to the FlexGym dark design system. Three regions:
 *   • Детали   — name + active pill, price/cost (Money), category, SKU/barcode,
 *                tax code.
 *   • Залиха   — per-location on-hand + the "Промена на залиха" adjust button.
 *   • Actions  — footer row: Edit, Deactivate/Reactivate, Delete (destructive).
 * Writes (deactivate / delete) go through the api-client accessors and are gated
 * to owner/manager via RLS + `hasRole`.
 */
import { deleteProduct, getProductInventory, updateProduct } from '@gym/api-client';
import { ApiError } from '@gym/shared';
import type { TaxRateCode } from '@gym/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Pencil, Power, Trash2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/Button.js';
import { SideDrawer } from '../../components/ui/Drawer.js';
import { Skeleton } from '../../components/ui/Feedback.js';
import { ConfirmModal } from '../../components/ui/Modal.js';
import { Money } from '../../components/ui/Money.js';
import { Chip, StatusPill } from '../../components/ui/StatusPill.js';
import { toast } from '../../components/ui/Toaster.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

import { ProductFormModal } from './ProductFormModal.js';
import { StockAdjustModal } from './StockAdjustModal.js';

interface ProductRow {
  id: string;
  name: string;
  price: number;
  cost: number | null;
  category: string | null;
  sku: string | null;
  barcode: string | null;
  tax_rate_code: TaxRateCode;
  is_active: boolean;
  track_inventory: boolean;
}

/** Minimal shape of an inventory_items row used within this file. */
interface InvRow {
  id: string;
  location_id: string | null;
  quantity_on_hand: number;
  reorder_level: number | null;
}

export interface ProductDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: ProductRow | null;
  tenantId: string;
}

/** Two-column label/value row inside a section card. */
function DetailRow({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <span className="shrink-0 text-sm text-muted">{label}</span>
      <span className="text-right text-sm font-medium text-fg">{children}</span>
    </div>
  );
}

/** Section heading with an optional leading icon. */
function SectionHeader({
  icon: Icon,
  children,
}: {
  icon?: typeof Package;
  children: ReactNode;
}): ReactNode {
  return (
    <h4 className="mb-2.5 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-muted">
      {Icon ? <Icon className="h-4 w-4" aria-hidden /> : null}
      {children}
    </h4>
  );
}

export function ProductDetailDrawer({
  open,
  onOpenChange,
  product,
  tenantId,
}: ProductDetailDrawerProps): ReactNode {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const canManageProducts = hasRole('owner', 'manager');
  const [editOpen, setEditOpen] = useState(false);
  const [adjustItem, setAdjustItem] = useState<InvRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const inventoryQ = useQuery({
    queryKey: ['product_inventory', product?.id],
    enabled: open && product != null,
    queryFn: () => getProductInventory(supabase, product!.id),
  });

  // ── Deactivate / Reactivate ────────────────────────────────────────────────
  const toggleActive = useMutation({
    mutationFn: () => updateProduct(supabase, product!.id, { is_active: !product!.is_active }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      toast.success(
        product!.is_active ? t('retail.product.deactivated') : t('retail.product.reactivate'),
      );
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(t('common.error'), e.message),
  });

  // ── Delete (from everywhere; sales history preserved) ───────────────────────
  const remove = useMutation({
    mutationFn: () => deleteProduct(supabase, product!.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['inventory_qty'] });
      void qc.invalidateQueries({ queryKey: ['low_stock'] });
      setConfirmDelete(false);
      toast.success(t('retail.product.deleted'));
      onOpenChange(false);
    },
    onError: (e: unknown) => {
      setConfirmDelete(false);
      // A restrict on purchase_order_items surfaces as ApiError('conflict').
      if (ApiError.is(e) && e.code === 'conflict') {
        toast.error(t('retail.product.deleteHasRefs'));
      } else {
        toast.error(t('common.error'), e instanceof Error ? e.message : undefined);
      }
    },
  });

  if (!product) return null;

  const busy = toggleActive.isPending || remove.isPending;
  const primaryInv = (inventoryQ.data ?? [])[0] ?? null;

  return (
    <>
      <SideDrawer
        open={open}
        onOpenChange={onOpenChange}
        title={product.name}
        subtitle={product.category ?? undefined}
        width="xl"
        footer={
          canManageProducts ? (
            <div className="flex items-center justify-end gap-2">
              {product.track_inventory ? (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy || !primaryInv}
                  onClick={() => primaryInv && setAdjustItem(primaryInv)}
                >
                  <Package className="h-4 w-4" aria-hidden />
                  {t('retail.adjustStock.title')}
                </Button>
              ) : null}
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setEditOpen(true)}
              >
                <Pencil className="h-4 w-4" aria-hidden />
                {t('common.edit')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                loading={toggleActive.isPending}
                disabled={remove.isPending}
                onClick={() => toggleActive.mutate()}
              >
                <Power className="h-4 w-4" aria-hidden />
                {product.is_active
                  ? t('retail.product.deactivate')
                  : t('retail.product.reactivate')}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={busy}
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                {t('retail.product.delete')}
              </Button>
            </div>
          ) : undefined
        }
      >
        {/* ── Детали ───────────────────────────────────────────────────────── */}
        <section>
          <SectionHeader>{t('retail.product.details')}</SectionHeader>

          <div className="rounded-xl border border-border bg-surface p-4">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-display text-lg font-semibold text-fg">{product.name}</h3>
                <Money minor={product.price} className="mt-0.5 font-mono text-base font-semibold" />
              </div>
              <StatusPill
                status={product.is_active ? 'active' : 'inactive'}
                label={product.is_active ? t('common.active') : t('common.inactive')}
              />
            </div>

            <div className="-mx-4 divide-y divide-border border-t border-border">
              {product.cost != null ? (
                <DetailRow label={t('retail.product.cost')}>
                  <Money minor={product.cost} className="font-mono" />
                </DetailRow>
              ) : null}

              {product.category ? (
                <DetailRow label={t('retail.product.category')}>
                  <Chip tone="neutral">{product.category}</Chip>
                </DetailRow>
              ) : null}

              {product.sku ? (
                <DetailRow label={t('retail.product.sku')}>
                  <span className="font-mono">{product.sku}</span>
                </DetailRow>
              ) : null}

              {product.barcode ? (
                <DetailRow label={t('retail.product.barcode')}>
                  <span className="font-mono">{product.barcode}</span>
                </DetailRow>
              ) : null}

              <DetailRow label={t('retail.product.tax_rate_code')}>
                <Chip tone="neutral">{product.tax_rate_code}</Chip>
              </DetailRow>

              <DetailRow label={t('retail.product.trackInventory')}>
                {product.track_inventory ? t('common.active') : t('common.inactive')}
              </DetailRow>
            </div>
          </div>
        </section>

        {/* ── Залиха ───────────────────────────────────────────────────────── */}
        {product.track_inventory ? (
          <section className="mt-6">
            <SectionHeader icon={Package}>{t('retail.stock')}</SectionHeader>

            <div className="overflow-hidden rounded-xl border border-border bg-surface">
              {inventoryQ.isLoading ? (
                <div className="space-y-2 p-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ) : (inventoryQ.data ?? []).length === 0 ? (
                <p className="p-4 text-sm text-muted">{t('retail.noStock')}</p>
              ) : (
                <div className="divide-y divide-border">
                  {(inventoryQ.data ?? []).map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between gap-3 px-4 py-2.5"
                    >
                      <span className="text-sm text-muted">
                        {item.location_id ?? t('retail.stock')}
                      </span>
                      <Chip
                        tone={
                          item.quantity_on_hand === 0
                            ? 'danger'
                            : item.reorder_level != null &&
                                item.quantity_on_hand <= item.reorder_level
                              ? 'warning'
                              : 'neutral'
                        }
                      >
                        <span className="font-mono">{item.quantity_on_hand}</span>
                      </Chip>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        ) : null}
      </SideDrawer>

      {/* Edit form (nested modal) */}
      <ProductFormModal
        open={editOpen}
        onOpenChange={(o) => {
          setEditOpen(o);
          if (!o) onOpenChange(false); // close drawer after a successful edit
        }}
        product={product}
        tenantId={tenantId}
      />

      {/* Delete confirmation */}
      <ConfirmModal
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('retail.product.delete')}
        description={t('retail.product.confirmDelete')}
        confirmLabel={t('retail.product.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />

      {/* Stock adjustment modal — opened per inventory row */}
      {adjustItem ? (
        <StockAdjustModal
          open={adjustItem != null}
          onOpenChange={(o) => {
            if (!o) setAdjustItem(null);
          }}
          productId={product.id}
          inventoryItemId={adjustItem.id}
          currentQty={adjustItem.quantity_on_hand}
        />
      ) : null}
    </>
  );
}
